<template>
  <v-card height="100%">
    <v-card-title>
      Details
    </v-card-title>

    <!-----------------------< LOADERS START >----------------------->
    <v-card-text v-if="currentTicketLoading">
      <v-row>

        <v-col cols="12" md="2">
          <v-skeleton-loader type="paragraph"></v-skeleton-loader>
        </v-col>

        <v-col cols="12" md="10">
          <v-row>
            <v-col cols="12" md="6" class="pt-0">

              <v-row>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
                <v-col cols="12" md="4" class="pt-0"><v-skeleton-loader type="paragraph"></v-skeleton-loader></v-col>
              </v-row>

            </v-col>
            
            <v-col cols="12" md="6" class="pt-0">
              <v-skeleton-loader type="paragraph"></v-skeleton-loader>
            </v-col>
          </v-row>
        </v-col>

      </v-row>
    </v-card-text>
    <!----------------------< LOADERS END >----------------------->

    <v-card-text v-else>
      <v-row>
        <v-col cols="12" md="4" lg="3">
          <template v-if="!statusChangeOpen">
            <p class="mb-1 text-caption">Status <v-icon small @click="openStatusChange()">edit</v-icon></p>
            <p class="display-1 accent--text">{{currentTicket.status | customPlaceholder}}</p>
          </template>

          <template v-else>
            <v-row>
              <v-col cols="8" :class="statusChangeOpen?'pb-2':''"><v-select v-model="status" :items="statuses" label="Select Status" dense outlined hide-details :disabled="ticketUpdateLoading" :loading="ticketUpdateLoading"></v-select></v-col>
              <v-col cols="2" class="mt-2"><v-icon :disabled="ticketUpdateLoading" :loading="ticketUpdateLoading" color="secondary" @click="checkStatus">check</v-icon></v-col>
              <v-col cols="2" class="mt-2"><v-icon :disabled="ticketUpdateLoading" :loading="ticketUpdateLoading" color="error" @click="statusChangeOpen=false;">close</v-icon></v-col>
            </v-row>
          </template>
        </v-col>
      
        <v-col cols="12" md="2">
          <p class="mb-1 text-caption">Resolution</p>
          <p><b>{{ currentTicket.resolution | customPlaceholder }}</b></p>
        </v-col>

        <v-col cols="12" md="2">
          <p class="mb-1 text-caption">Time Open</p>
          <p class="display-1 accent--text">{{currentTicket.creation_timestamp | timeOpen}}</p>
        </v-col>

        <v-col cols="12" md="2">
          <p class="mb-1 text-caption">Escalated</p>
          <p><b>{{((currentTicket.escalated)?'Yes':'No')}}</b></p>
        </v-col>

        <v-col cols="12" md="2">
          <p class="mb-1 text-caption">Freshdesk Ticket ID</p>
          <p><b><a :href="`${freshdeskTicketsDomain}/${currentTicket.freshdesk_id}`" target="_blank">{{currentTicket.freshdesk_id | customPlaceholder}}</a></b></p>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="4" lg="3">
          <p class="mb-1 text-caption">Created</p>
          <p>
            <b>{{currentTicket.creation_timestamp | formatDate}}</b><br>
            <b>{{currentTicket.created_by}}</b>
          </p>
        </v-col>
        
        <v-col cols="12" md="2">
          <p class="mb-1 text-caption">Product ID</p>
          <p><b><a :href="`/products/${currentTicket.product_id}`" target="_blank">{{currentTicket.product_id | customPlaceholder}}</a></b></p>
        </v-col>

        <v-col cols="12" md="3">
          <template v-if="!editTicketTypeOpen">
            <p class="mb-1 text-caption">Ticket Type <v-icon small :disabled="!hasUserGotRole('can-change-ticket-type')" @click="editTicketType">edit</v-icon></p>
            <p><b>{{ currentTicket.kind | customPlaceholder }}</b></p>
          </template>
          <template v-else>
            <div class="d-flex justify-start align-center">

              <v-select v-model="kind" label="Ticket type" :items="getTicketTypes" :disabled="getTicketUpdateLoading" hide-details dense outlined />

              <div class="d-flex align-center">
                <v-icon color="secondary" :disabled="getTicketUpdateLoading" @click="updateTicketType">check</v-icon>
                <v-icon color="error" :disabled="getTicketUpdateLoading" @click="editTicketTypeOpen=false">close</v-icon>
              </div>

            </div>
          </template>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <p class="mb-1 text-caption">Description</p>
          <p><b v-html="nl2br(currentTicket.description)"></b></p>
        </v-col>
      </v-row>

      <CloseTicketDialog :open="closeTicketOpen" @closeDialog="closeTicketOpen=false" @updateStatus="updateStatus"/>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions } from "vuex";

import CloseTicketDialog from '@/components/Tickets/Dialogs/CloseTicketDialog';
import { freshdeskTicketsDomain } from '@/helpers/links';

export default {
  components: {CloseTicketDialog},
  
  data() {
    return {
      statusChangeOpen: false,
      statuses: [{text:'New',value:'new'},{text:'Open',value:'open'},{text:'Pending',value:'pending'},{text:'Closed',value:'closed'},{text:'Escalation',value:'escalation'}],
      status: '',
      kind: '',
      closeTicketOpen: false,
      editTicketTypeOpen: false,
      freshdeskTicketsDomain,
    }
  },

  computed: {
    ...mapState('tickets', ['currentTicket','currentTicketLoading','ticketUpdateLoading']),
    ...mapGetters('tickets', ['getTicketTypes', 'getTicketUpdateLoading']),
    ...mapGetters('globals', ['hasUserGotRole']), 
  },

  filters:{
    timeOpen(val){
      var a = moment(new Date());
      var b = moment(val);
      let days = a.diff(b, 'days');
      let hours = a.diff(b, 'hours')%24;
      return `${days}d ${hours}h`;
    },
  },

  methods: {
    ...mapActions('tickets',['updateTicketData','fetchTicketByID']),

    checkStatus() {
      if (this.status === 'closed') {
        this.closeTicketOpen = true;
      } else {
        this.updateStatus('');
      }
    },

    async updateStatus(resolution){
      const id = this.$route.params.id;
      const data = {status: this.status}

      if (this.status === 'closed') {
        data.resolution = resolution;
      }
      const success = await this.updateTicketData({id: id, data: id, data});
      if(success){
        this.statusChangeOpen = false;
        this.fetchTicketByID(id);
      }
    },

    openStatusChange(){
      this.statusChangeOpen = true;
    },
    
    nl2br(str, is_xhtml) {
      if(typeof str === 'undefined' || str === null){ return ''; }
      var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>';
      return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
    },

    editTicketType() {
      this.kind = this.currentTicket.kind;
      this.editTicketTypeOpen = true;
    },

    async updateTicketType() {
      const success = await this.updateTicketData({id: this.currentTicket.id, data: {kind: this.kind}});

      if (success) {
        this.editTicketTypeOpen = false;
        this.fetchTicketByID(this.currentTicket.id);
      }
    }
  },

  watch: {
    statusChangeOpen(val) {
      if(val){ this.status = this.currentTicket.status; }
    }
  },
};
</script>

<style lang="scss" scoped></style>
